import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { mdiAlertCircleOutline, mdiCheckCircle } from '@mdi/js';
import Icon from '@mdi/react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { classApi } from '../../../api/classes';
import { accountApi } from '../../../api/account';
import { useAuth } from '../../../contexts/AuthContext';
import BaseModal from '../../../components/BaseModal';
import { formatCurrencyFromMinor } from '../../../utils/currency';
import { isOrgPaymentsNotReady, orgPaymentsNotReadyMessage } from '../../../utils/orgPayments';
import { isOrgDomainClosed } from '../../../utils/orgDomainClosed';
import './TrialDealLandingPage.css';
import { getStripePromise } from "../../../utils/stripeKey";
import ServiceFeeInfo from "../../../components/ServiceFeeInfo";


const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatSessionDate(isoDate) {
    if (!isoDate) return null;
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayName = DAYS[date.getDay()];
    const d = date.getDate();
    const suffix = d === 1 || d === 21 || d === 31 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';
    const monthName = date.toLocaleString('en', { month: 'long' });
    return `${dayName} ${d}${suffix} ${monthName}`;
}

const HOW_DID_YOU_HEAR_OPTIONS = [
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'google', label: 'Google Search' },
    { value: 'friend', label: 'Friend / Word of mouth' },
    { value: 'walkin', label: 'Walked past / saw a flyer' },
    { value: 'other', label: 'Other' },
];

// Cents → currency string. Mirrors booking + class + ticket payment-page convention.
function formatAmount(cents, currency) {
    return formatCurrencyFromMinor(cents, currency);
}

// PaymentForm — must be a child of <Elements> so Stripe hooks work
function PaymentForm({ fees, onSuccess }) {
    const stripe = useStripe();
    const elements = useElements();
    const [isPaying, setIsPaying] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [elementsReady, setElementsReady] = useState(false);

    const handlePay = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || !elementsReady) return;
        setIsPaying(true);
        setErrorMessage('');

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: 'if_required',
            });

            if (error) {
                setErrorMessage(error.message || 'Payment failed. Please try again.');
                setIsPaying(false);
                return;
            }

            if (paymentIntent && paymentIntent.status === 'succeeded') {
                onSuccess();
            } else {
                setErrorMessage('Payment is processing. Watch your inbox for confirmation.');
                setIsPaying(false);
            }
        } catch {
            setErrorMessage('Payment failed. Please try again.');
            setIsPaying(false);
        }
    };

    // Service fee = platformFee + processingFee (combined, single line — never a breakdown)
    const serviceFeeCents = (fees?.platformFee || 0) + (fees?.processingFee || 0);
    const baseCents = fees?.basePrice || 0;
    const totalCents = fees?.totalPrice || 0;
    const currency = fees?.currency;

    return (
        <form className="tdlp-payment-form" onSubmit={handlePay}>
            {fees && (
                <div className="tdlp-fee-breakdown">
                    <div className="tdlp-fee-row">
                        <span>Trial fee</span>
                        <span>{formatAmount(baseCents, currency)}</span>
                    </div>
                    {serviceFeeCents > 0 && (
                        <div className="tdlp-fee-row">
                            <ServiceFeeInfo />
                            <span>{formatAmount(serviceFeeCents, currency)}</span>
                        </div>
                    )}
                    <div className="tdlp-fee-total">
                        <span>Total Due</span>
                        <span>{formatAmount(totalCents, currency)}</span>
                    </div>
                </div>
            )}

            <PaymentElement onReady={() => setElementsReady(true)} onLoadError={(e) => setErrorMessage(e?.error?.message || 'Could not load the payment form. Please try again.')} options={{ layout: 'tabs', paymentMethodOrder: ['apple_pay', 'google_pay', 'card'] }} />
            {errorMessage && <p className="tdlp-payment-error">{errorMessage}</p>}
            <button
                type="submit"
                className="tdlp-submit-btn"
                disabled={!stripe || !elements || !elementsReady || isPaying}
            >
                {isPaying ? 'Processing...' : `Pay ${formatAmount(totalCents, currency)}`}
            </button>
            <p className="tdlp-secure-note">Secure payment via Stripe. Your card details are never stored.</p>
        </form>
    );
}

const TrialDealLandingPage = () => {
    const { slug } = useParams();
    // Public page, so a visitor is usually a guest — but an existing member buying a
    // trial for themselves should not retype details we already hold.
    const { user } = useAuth();
    const [deal, setDeal] = useState(null);
    // Checkout price (cents) from the server, same calculation the purchase uses, so the
    // Service fee and total show BEFORE the buyer presses Buy.
    const [checkoutFees, setCheckoutFees] = useState(null);
    const [eligibleClasses, setEligibleClasses] = useState([]);
    const [orgName, setOrgName] = useState('');
    const [orgLogoUrl, setOrgLogoUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isExpired, setIsExpired] = useState(false);
    const [notFound, setNotFound] = useState(false);
    // Set when the school has switched class management off (ORG_DOMAIN_CLOSED).
    const [closedMessage, setClosedMessage] = useState('');

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [leadSource, setLeadSource] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [warningModal, setWarningModal] = useState(null);

    // Payment-stage state: 'form' (initial) | 'paying' (Elements rendered) | 'success' (post-payment)
    const [paymentStage, setPaymentStage] = useState('form');
    const [clientSecret, setClientSecret] = useState('');
    const [paymentFees, setPaymentFees] = useState(null);
    // Held for potential debug surfacing; not currently displayed.
    const [, setPaymentIntentId] = useState('');

    const utmRef = useRef({});
    const hasUtmSource = useRef(false);
    const rootRef = useRef(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const utm = {};
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(k => {
            const v = params.get(k);
            if (v) utm[k.replace('utm_', '')] = v;
        });
        utmRef.current = utm;
        hasUtmSource.current = !!utm.source;
    }, []);

    // Pre-populate the form when the visitor is logged in. Mirrors the booking flow
    // (BookingConfirmation): name off the account profile, email off the JWT, because
    // email lives on the auth document and is not part of the account profile payload.
    // Phone is not on the profile at all, so it stays blank for everyone. Fields remain
    // editable — someone may well be buying the trial for a partner or child.
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        accountApi.getProfile()
            .then(profile => {
                if (cancelled) return;
                if (profile.firstName) setFirstName(profile.firstName);
                if (profile.lastName) setLastName(profile.lastName);
                if (user.email) setEmail(user.email);
            })
            .catch(() => {
                // Profile fetch failed — still prefill the email we already have on the JWT.
                if (!cancelled && user.email) setEmail(user.email);
            });
        return () => { cancelled = true; };
    }, [user]);

    useEffect(() => {
        const load = async () => {
            try {
                const r = await classApi.getPublicTrialDeal(slug);
                if (!r.ok) {
                    if (isOrgDomainClosed(r)) {
                        setClosedMessage(r.data.message);
                    } else {
                        setNotFound(true);
                    }
                    return;
                }
                if (r.data.expired) {
                    setIsExpired(true);
                    setDeal(r.data.trialDeal);
                    return;
                }
                setDeal(r.data.trialDeal);
                setCheckoutFees(r.data.fees || null);
                setEligibleClasses(r.data.eligibleClasses || []);
                setOrgName(r.data.orgName || '');
                setOrgLogoUrl(r.data.orgLogoUrl || null);

                const branding = r.data.branding || {};
                const root = rootRef.current;
                if (root) {
                    if (branding.headerColor)     root.style.setProperty('--school-header-color', branding.headerColor);
                    if (branding.headerTextColor) root.style.setProperty('--school-header-text',  branding.headerTextColor);
                    if (branding.buttonColor)     root.style.setProperty('--school-btn-color',    branding.buttonColor);
                    if (branding.buttonTextColor) root.style.setProperty('--school-btn-text',     branding.buttonTextColor);
                    if (branding.backgroundColor) root.style.setProperty('--school-bg-color',     branding.backgroundColor);
                }
            } catch {
                setNotFound(true);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [slug]);

    const buildBody = () => {
        const body = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            phone: phone.trim() || undefined,
            utm: utmRef.current,
        };
        if (!hasUtmSource.current && leadSource) body.leadSource = leadSource;
        return body;
    };

    const proceedWithBuy = async (body) => {
        setIsSubmitting(true);
        try {
            const r = await classApi.createTrialPurchaseIntent(slug, body);
            if (r.ok && r.data?.clientSecret) {
                setClientSecret(r.data.clientSecret);
                setPaymentIntentId(r.data.paymentIntentId);
                setPaymentFees(r.data.fees || null);
                setPaymentStage('paying');
                return;
            }
            if (r.status === 410) { setIsExpired(true); return; }
            if (r.status === 409) {
                toast.error(r.data?.message || 'You already have this trial.');
                return;
            }
            if (isOrgPaymentsNotReady(r)) {
                toast.error(orgPaymentsNotReadyMessage(orgName, 'the school'));
                return;
            }
            toast.error(r.data?.message || 'Something went wrong. Please try again.');
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!firstName.trim()) { toast.error('First name is required'); return; }
        if (!lastName.trim())  { toast.error('Last name is required');  return; }
        if (!email.trim())     { toast.error('Email is required');      return; }

        const body = buildBody();

        try {
            const check = await classApi.checkTrialDealEmail(slug, body.email);
            if (check.ok) {
                const { expired, existingStudent, existingPurchase } = check.data;
                if (expired) { setIsExpired(true); return; }

                const messages = [];
                if (existingPurchase) messages.push(`This email has already purchased ${deal?.name || 'this trial deal'}.`);
                if (existingStudent)  messages.push(`This email is already a student at ${orgName || 'this school'}.`);

                if (messages.length > 0) {
                    setWarningModal({ messages, pendingBody: body });
                    return;
                }
            }
        } catch {
            // Pre-check failed — proceed; non-critical
        }

        await proceedWithBuy(body);
    };

    const handleWarningConfirm = async () => {
        const body = warningModal?.pendingBody;
        setWarningModal(null);
        if (body) await proceedWithBuy(body);
    };

    if (isLoading) {
        return (
            <div className="tdlp-page" ref={rootRef}>
                <div className="tdlp-loading">Loading...</div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="tdlp-page tdlp-page--centered" ref={rootRef}>
                <h1 className="tdlp-status-title">Offer Not Found</h1>
                <p className="tdlp-status-body">This trial offer could not be found.</p>
                <a className="tdlp-home-link" href="https://dancelocale.com">Browse Dance Locale</a>
            </div>
        );
    }

    if (closedMessage) {
        return (
            <div className="tdlp-page tdlp-page--centered" ref={rootRef}>
                <h1 className="tdlp-status-title">Trial Offer</h1>
                <p className="tdlp-status-body">{closedMessage}</p>
                <a className="tdlp-home-link" href="https://dancelocale.com">Browse Dance Locale</a>
            </div>
        );
    }

    if (isExpired) {
        return (
            <div className="tdlp-page tdlp-page--centered" ref={rootRef}>
                <h1 className="tdlp-status-title">{deal?.name || 'Trial Offer'}</h1>
                <p className="tdlp-status-body">This trial offer has ended.</p>
                <a className="tdlp-home-link" href="https://dancelocale.com">Browse Dance Locale</a>
            </div>
        );
    }

    const priceStr = deal.price != null
        ? formatCurrencyFromMinor(deal.price, deal.currency)
        : null;
    const serviceFeeCents = checkoutFees ? checkoutFees.platformFee + checkoutFees.processingFee : 0;
    const totalStr = checkoutFees ? formatCurrencyFromMinor(checkoutFees.totalPrice, checkoutFees.currency) : null;

    const ctaLabel = deal.landingContent?.ctaLabel || 'Book Now';

    // Resolve Stripe appearance colour via getComputedStyle — Stripe iframe cannot read CSS vars directly.
    // The '#451f78' literal is the RESOLVED value of --dl-color-purple-default (per project rule:
    // documented runtime fallback for the Stripe-iframe-can't-read-CSS-vars constraint, not an invented colour).
    const primaryColor = (typeof window !== 'undefined' && document.documentElement)
        ? (getComputedStyle(document.documentElement).getPropertyValue('--dl-color-purple-default').trim() || '#451f78')
        : '#451f78';

    return (
        <div className="tdlp-page" ref={rootRef}>
            <header className="tdlp-header">
                {orgLogoUrl && (
                    <img src={orgLogoUrl} alt={orgName} className="tdlp-header-logo" />
                )}
                <div className="tdlp-header-text">
                    <h1 className="tdlp-header-org">{orgName || 'Dance Locale'}</h1>
                    <p className="tdlp-header-subtitle">Trial Offer</p>
                </div>
            </header>

            <div className="tdlp-content">
                <h2 className="tdlp-deal-title">{deal.name}</h2>

                {(totalStr || priceStr) && (
                    <p className="tdlp-price">
                        {totalStr || priceStr}
                        {totalStr && serviceFeeCents > 0 && <span className="tdlp-price-includes">includes service fee</span>}
                    </p>
                )}

                {/* Only when there is something to skip: the school's description and/or the
                    eligible classes list sit between this link and the booking form. */}
                {paymentStage === 'form' && (deal.landingContent?.bodyHtml || eligibleClasses.length > 0) && (
                    <a
                        href="#tdlp-buy-form"
                        className="tdlp-skip-link"
                        onClick={(e) => {
                            e.preventDefault();
                            const el = document.getElementById('tdlp-buy-form');
                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                    >
                        Skip to Book Now ↓
                    </a>
                )}

                {deal.landingContent?.bodyHtml && (
                    <p className="tdlp-description">{deal.landingContent.bodyHtml}</p>
                )}

                {eligibleClasses.length > 0 && (
                    <div className="tdlp-classes">
                        <p className="tdlp-classes-heading">Included classes</p>
                        <ul className="tdlp-class-list">
                            {eligibleClasses.map(cls => (
                                <li key={cls.id} className="tdlp-class-item">
                                    <span className="tdlp-class-name">{cls.name}</span>
                                    <span className="tdlp-class-meta">
                                        {cls.firstSessionDate
                                            ? `${formatSessionDate(cls.firstSessionDate)}${cls.startTime ? ` at ${cls.startTime}` : ''}`
                                            : cls.dayOfWeek != null
                                                ? `${DAYS[cls.dayOfWeek]}${cls.startTime ? ` ${cls.startTime}` : ''}`
                                                : cls.startTime || ''
                                        }
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {paymentStage === 'form' && (
                    <form className="tdlp-form" id="tdlp-buy-form" onSubmit={handleSubmit}>
                        <h3 className="tdlp-form-heading">Book your spot</h3>

                        <div className="tdlp-form-row">
                            <div className="tdlp-form-field">
                                <label className="tdlp-label" htmlFor="tdlp-first-name">First name *</label>
                                <input
                                    id="tdlp-first-name"
                                    className="tdlp-input"
                                    type="text"
                                    autoComplete="given-name"
                                    placeholder="First name"
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="tdlp-form-field">
                                <label className="tdlp-label" htmlFor="tdlp-last-name">Last name *</label>
                                <input
                                    id="tdlp-last-name"
                                    className="tdlp-input"
                                    type="text"
                                    autoComplete="family-name"
                                    placeholder="Last name"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="tdlp-form-field">
                            <label className="tdlp-label" htmlFor="tdlp-email">Email *</label>
                            <input
                                id="tdlp-email"
                                className="tdlp-input"
                                type="email"
                                autoComplete="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                            />
                        </div>

                        <div className="tdlp-form-field">
                            <label className="tdlp-label" htmlFor="tdlp-phone">Phone</label>
                            <input
                                id="tdlp-phone"
                                className="tdlp-input"
                                type="tel"
                                autoComplete="tel"
                                placeholder="0400 000 000"
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                            />
                        </div>

                        {!hasUtmSource.current && (
                            <div className="tdlp-form-field">
                                <label className="tdlp-label" htmlFor="tdlp-lead-source">How did you hear about us?</label>
                                <select
                                    id="tdlp-lead-source"
                                    className="tdlp-input tdlp-select"
                                    value={leadSource}
                                    onChange={e => setLeadSource(e.target.value)}
                                >
                                    <option value="">Select one (optional)</option>
                                    {HOW_DID_YOU_HEAR_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {checkoutFees && (
                            <div className="tdlp-fee-breakdown">
                                <div className="tdlp-fee-row">
                                    <span>Trial fee</span>
                                    <span>{formatCurrencyFromMinor(checkoutFees.basePrice, checkoutFees.currency)}</span>
                                </div>
                                {serviceFeeCents > 0 && (
                                    <div className="tdlp-fee-row">
                                        <ServiceFeeInfo />
                                        <span>{formatCurrencyFromMinor(serviceFeeCents, checkoutFees.currency)}</span>
                                    </div>
                                )}
                                <div className="tdlp-fee-total">
                                    <span>Total</span>
                                    <span>{totalStr}</span>
                                </div>
                            </div>
                        )}

                        <button
                            className="tdlp-submit-btn"
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Processing...' : (totalStr ? `${ctaLabel} (${totalStr})` : ctaLabel)}
                        </button>

                        <p className="tdlp-secure-note">Secure payment via Stripe. You will not leave dancelocale.com.</p>
                    </form>
                )}

                {paymentStage === 'paying' && clientSecret && (
                    <div className="tdlp-payment-section">
                        <h3 className="tdlp-form-heading">Complete your payment</h3>
                        <Elements
                            stripe={getStripePromise()}
                            options={{
                                clientSecret,
                                appearance: {
                                    theme: 'stripe',
                                    variables: {
                                        colorPrimary: primaryColor,
                                        colorBackground: '#ffffff',
                                        colorText: '#1a1a1a',
                                        borderRadius: '8px',
                                        fontFamily: 'system-ui, sans-serif',
                                    },
                                },
                            }}
                        >
                            <PaymentForm
                                fees={paymentFees}
                                onSuccess={() => setPaymentStage('success')}
                            />
                        </Elements>
                    </div>
                )}

                {paymentStage === 'success' && (
                    <div className="tdlp-success-card">
                        <Icon path={mdiCheckCircle} size={2.2} color="var(--dl-color-green-500)" />
                        <h3 className="tdlp-success-title">Payment confirmed</h3>
                        <p className="tdlp-success-body">
                            Watch your inbox for class details from {orgName || 'the studio'}.
                        </p>
                    </div>
                )}

                <p className="tdlp-powered">Powered by <a href="https://dancelocale.com">Dance Locale</a></p>
            </div>

            <BaseModal
                isOpen={!!warningModal}
                onClose={() => setWarningModal(null)}
                title="Just so you know"
                icon={mdiAlertCircleOutline}
                maxWidth={420}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={() => setWarningModal(null)}>Cancel</button>
                        <button className="base-modal-save-btn" onClick={handleWarningConfirm} disabled={isSubmitting}>
                            {isSubmitting ? 'Processing...' : 'Yes, continue'}
                        </button>
                    </>
                }
            >
                <ul className="tdlp-modal-messages">
                    {(warningModal?.messages || []).map((msg, i) => (
                        <li key={i}>{msg}</li>
                    ))}
                </ul>
                <p className="tdlp-modal-question">Do you still want to continue?</p>
            </BaseModal>
        </div>
    );
};

export default TrialDealLandingPage;
